import { useState, useEffect } from 'react'
import axios from 'axios'
import Header from '../components/shared/Header'
import Footer from '../components/shared/Footer'
import MetaTags from '../components/shared/MetaTags'
import StructuredData from '../components/shared/StructuredData'
import ShareButtons from '../components/shared/ShareButtons'

export default function BuildingSiteExperimentPage() {
  const [experiment, setExperiment] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showDevLog, setShowDevLog] = useState(false)

  useEffect(() => {
    const fetchExperiment = async () => {
      try {
        const response = await axios.get('/api/v1/experiments/building-this-site')
        setExperiment(response.data)
      } catch (error) {
        console.error('Failed to fetch experiment:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchExperiment()
  }, [])

  const renderSection = (section) => {
    switch (section.type) {
      case 'highlight':
        return (
          <div className="border-2 border-accent-dark rounded-large bg-accent-muted-dark p-6 md:p-8 mb-6 md:mb-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 text-accent-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">{section.title}</h2>
                {section.content.paragraphs.map((p, i) => (
                  <p key={i} className="text-muted-dark leading-relaxed mb-3 last:mb-0">{p}</p>
                ))}
              </div>
            </div>
          </div>
        )

      case 'numbered_list':
        return (
          <div className="border border-border-dark rounded-large bg-white/[0.04] p-6 md:p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-6">{section.title}</h2>
            <div className="space-y-6">
              {section.content.items.map((item, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold mb-2 flex items-start gap-2">
                    <span className="text-accent-dark flex-shrink-0">{index + 1}.</span>
                    <span>{item.title}</span>
                  </h3>
                  <p className="text-muted-dark leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        )

      case 'grid':
        return (
          <div className="border border-border-dark rounded-large bg-white/[0.04] p-6 md:p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4">{section.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {section.content.columns.map((column, index) => (
                <div key={index}>
                  <h3 className="font-semibold mb-3 text-accent-dark">{column.title}</h3>
                  <ul className="space-y-2 text-sm text-muted-dark">
                    {column.items.map((item, i) => {
                      const [name, ...rest] = item.split(' - ')
                      const desc = rest.join(' - ')
                      return (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-accent-dark">•</span>
                          <span><strong className="text-text-dark">{name}</strong> - {desc}</span>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )

      case 'cost_breakdown':
        return (
          <div className="border border-border-dark rounded-large bg-white/[0.04] p-6 md:p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4">{section.title}</h2>
            <div className="space-y-3">
              {section.content.items.map((item, index) => (
                <div key={index} className="flex justify-between items-baseline">
                  <span className="text-muted-dark">{item.label}</span>
                  <span className="font-mono text-text-dark">{item.cost}</span>
                </div>
              ))}
              <div className="flex justify-between items-baseline pt-3 border-t border-border-dark">
                <span className="text-text-dark font-semibold">Total Monthly</span>
                <span className="font-mono text-accent-dark text-lg font-bold">{section.content.total}</span>
              </div>
            </div>
            <p className="text-sm text-muted-dark mt-4 leading-relaxed">
              {section.content.note}
            </p>
          </div>
        )

      case 'text':
        return (
          <div className="border border-border-dark rounded-large bg-white/[0.04] p-6 md:p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4">{section.title}</h2>
            <div className="space-y-4 text-muted-dark leading-relaxed">
              {section.content.paragraphs.map((p, i) => (
                <p key={i} className={i === 0 ? 'text-lg' : ''}>{p}</p>
              ))}
            </div>
          </div>
        )

      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-container mx-auto px-5 py-8">
          <Header />
          <div className="text-muted-dark text-center py-20">Loading...</div>
          <Footer />
        </div>
      </div>
    )
  }

  if (!experiment) {
    return (
      <div className="min-h-screen">
        <div className="max-w-container mx-auto px-5 py-8">
          <Header />
          <div className="text-center py-20">
            <h1 className="text-2xl mb-4">Experiment not found</h1>
          </div>
          <Footer />
        </div>
      </div>
    )
  }

  const statusColor = experiment.status.state === 'iterating' 
    ? 'border-[rgba(122,215,255,0.28)] bg-accent-muted-dark text-text-dark'
    : 'border-border-dark text-muted-dark bg-white/[0.02]'

  return (
    <>
      {experiment && (
        <>
          <MetaTags
            title={`${experiment.title} - Experiment ${String(experiment.number).padStart(3, '0')} | deedslab.com`}
            description={experiment.description || "An experiment in building and learning from real-world testing."}
            url={`/experiments/${experiment.slug}`}
            type="article"
          />
          <StructuredData
            type="Article"
            title={`${experiment.title} - Experiment ${String(experiment.number).padStart(3, '0')} | deedslab.com`}
            description={experiment.description || "An experiment in building and learning from real-world testing."}
            url={`/experiments/${experiment.slug}`}
            datePublished={experiment.created_at}
            dateModified={experiment.updated_at}
            experimentData={experiment}
          />
        </>
      )}
      <div
        className="min-h-screen"
        style={{
          background: 'radial-gradient(900px 520px at 20% -10%, rgba(122, 215, 255, 0.18), transparent 58%)'
        }}
      >
      <div className="max-w-container mx-auto px-5 md:px-8 py-8">
        <Header />

        {/* Experiment Header - Loaded from MongoDB */}
        <header className="mb-6 md:mb-8">
          <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4 flex-wrap">
            <span className="font-mono text-xs tracking-wide uppercase text-muted-dark">
              Experiment {String(experiment.number).padStart(3, '0')}
            </span>
            <span className={`font-mono text-[11px] tracking-wide uppercase py-1.5 px-2.5 rounded-full border ${statusColor}`}>
              {experiment.status.state.charAt(0).toUpperCase() + experiment.status.state.slice(1)}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-semibold mb-3 md:mb-4 leading-tight tracking-tight">
            {experiment.name}
          </h1>
          <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-muted-dark font-mono mb-4 md:mb-6 flex-wrap">
            <span>Initiated {experiment.initiated}</span>
            {experiment.status.last_signal && (
              <>
                <span>•</span>
                <span>Last signal {experiment.status.last_signal}</span>
              </>
            )}
          </div>
        </header>

        {/* Share Buttons */}
        <div className="flex justify-center sm:justify-start mb-6 md:mb-8">
          <ShareButtons
            title={`${experiment.title} - Experiment ${String(experiment.number).padStart(3, '0')} | deedslab.com`}
            description={experiment.description || "An experiment in building and learning from real-world testing."}
            url={`/experiments/${experiment.slug}`}
          />
        </div>

        {/* Experiment Phases - Loaded from MongoDB */}
        {experiment.phases && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
            <div className="border border-border-dark rounded-large bg-white/[0.04] p-4 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-muted-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <h3 className="font-mono text-xs tracking-wide uppercase text-muted-dark">Ideating</h3>
              </div>
              <div className="text-sm text-muted-dark leading-relaxed">
                {experiment.phases.ideating}
              </div>
            </div>

            <div className="border border-border-dark rounded-large bg-white/[0.04] p-4 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-accent-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <h3 className="font-mono text-xs tracking-wide uppercase text-muted-dark">Iterating</h3>
              </div>
              <div className="text-sm text-muted-dark leading-relaxed">
                {experiment.phases.iterating}
              </div>
            </div>

            <div className="border border-border-dark rounded-large bg-white/[0.04] p-4 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-muted-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="font-mono text-xs tracking-wide uppercase text-muted-dark">Learnings</h3>
              </div>
              <div className="text-sm text-muted-dark leading-relaxed">
                {experiment.phases.learnings}
              </div>
            </div>
          </div>
        )}

        {/* Dynamic Sections from MongoDB */}
        {experiment.sections && experiment.sections.map((section, index) => (
          <div key={index}>
            {renderSection(section)}
          </div>
        ))}

        {/* Development Log from MongoDB */}
        {experiment.dev_log && experiment.dev_log.length > 0 && (
          <div className="border border-border-dark rounded-large bg-white/[0.04] overflow-hidden mb-8">
            <button
              onClick={() => setShowDevLog(!showDevLog)}
              className="w-full px-6 md:px-8 py-5 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-muted-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h2 className="text-lg md:text-xl font-semibold">Development Log</h2>
                <span className="font-mono text-xs tracking-wide uppercase text-muted-dark hidden md:inline">Build Record</span>
              </div>
              <span className="text-2xl text-muted-dark transform transition-transform" style={{ transform: showDevLog ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                ▼
              </span>
            </button>
            
            {showDevLog && (
              <div className="px-6 md:px-8 pb-8">
                <div className="border-t border-border-dark pt-6">
                  <div className="space-y-6">
                    {experiment.dev_log.map((entry, index) => (
                      <div key={index} className={`border-l-2 ${index === 0 ? 'border-accent-dark' : 'border-muted-dark'} pl-6`}>
                        <div className="flex items-baseline gap-3 mb-2">
                          <span className={`font-mono text-xs ${index === 0 ? 'text-accent-dark' : 'text-muted-dark'}`}>
                            {entry.date}
                          </span>
                          <span className="font-mono text-xs uppercase tracking-wide text-muted-dark">
                            {entry.phase}
                          </span>
                        </div>
                        <h3 className="font-semibold mb-2">{entry.title}</h3>
                        <div className="text-sm text-muted-dark leading-relaxed space-y-2">
                          {entry.entries && entry.entries.length > 0 && entry.entries.map((item, i) => (
                            <p key={i}>
                              <strong className="text-text-dark">{item.label}:</strong> {item.text}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <Footer />
      </div>
    </div>
    </>
  )
}
